import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps, PickerValidDate } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { ReactNode, useCallback, useEffect, useState } from "react";

export interface DatePickerProps<TDate extends PickerValidDate> extends Omit<MuiDatePickerProps<TDate>, "value" | "onChange"> {
    value?: string,
    onChange?: (v: string) => void,
    onKeyDown?: React.KeyboardEventHandler,
    onBlur?: React.FocusEventHandler,
    displayDateOffset?: number,
    size?: "medium" | "small",
    clearable?: boolean,
    error?: boolean,
    helperText?: ReactNode,
}

export default function DatePicker<TDate extends PickerValidDate>({
    value,
    onChange,
    onBlur,
    onKeyDown,
    displayDateOffset = 0,
    clearable,
    size,
    error,
    helperText,
    ...props
}: DatePickerProps<TDate>) {
    const ValueToDate = useCallback((value: string | null | undefined): TDate | undefined | null => {
        if (value == null) return value;
        const ret = dayjs(value);
        if (!ret.isValid()) return null;
        return ret.add(displayDateOffset, "day") as TDate;
    }, [displayDateOffset]);

    const DateToValue = useCallback((value: TDate | null | undefined): string => {
        if (!value) return "";
        const date = dayjs(value);
        if (!date.isValid()) return "";
        const d = date.subtract(displayDateOffset, "day").toDate();
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }, [displayDateOffset]);

    const [dateValue, setDateValue] = useState<TDate | null | undefined>(ValueToDate(value));

    useEffect(() => {
        const newValue = dayjs(ValueToDate(value));
        const localValue = dayjs(dateValue);

        if (newValue.isValid()) {
            setDateValue(newValue as TDate);
        } else if (!newValue.isValid() && localValue.isValid()) {
            setDateValue(null);
        }
    }, [value]);

    return <MuiDatePicker
        {...props}
        value={dateValue as TDate}
        onChange={(v) => {
            setDateValue(v);
            onChange && onChange(DateToValue(v));
        }}

        slotProps={{
            ...props.slotProps,
            field: {
                ...props.slotProps?.field,
                clearable,
                onBlur,
                onKeyDown
            },
            textField: {
                ...props.slotProps?.textField,
                error,
                helperText,
                size
            }
        }}
    />
}